import { ContextMenuHandler, IMenuItem } from "@asup/context-menu";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorV3AtBlock } from "../classes/EditorV3AtBlock";
import { EditorV3Content } from "../classes/EditorV3Content";
import { EditorV3Line } from "../classes/EditorV3Line";
import { defaultContentProps } from "../classes/defaultContentProps";
import { IMarkdownSettings } from "../classes/defaultMarkdownSettings";
import {
  EditorV3Align,
  EditorV3ContentPropsInput,
  EditorV3DropListItem,
  EditorV3Position,
  EditorV3Styles,
  IEditorV3,
} from "../classes/interface";
import { cleanIsEqual, useDebounceStack } from "../hooks";
import "./EditorV3.css";
import { WindowView } from "./WindowView/WindowView";

/**
 * Checks if the content has any active unlocked at-mention blocks.
 *
 * @param currentValue - The editor content to check
 * @returns True if there are active at blocks
 */
const hasActiveAt = (currentValue: EditorV3Content): boolean => {
  return currentValue.lines.some((i) =>
    i.textBlocks.some((tb) => tb.type === "at" && !tb.isLocked),
  );
};

/**
 * Checks if two EditorV3 content values have the same text content.
 *
 * @param oldValue - The previous EditorV3 content value
 * @param newValue - The new EditorV3 content value to compare
 */
const sameText = (oldValue: EditorV3Content, newValue: EditorV3Content): boolean =>
  // Lines data and showMarkdown are the only properties that should be compared
  cleanIsEqual(
    {
      lines: oldValue.lines.map((l) => l.textBlocks.map((tb) => tb.data)),
      showMarkdown: oldValue?.showMarkdown,
    },
    {
      lines: newValue.lines.map((l) => l.textBlocks.map((tb) => tb.data)),
      showMarkdown: newValue.showMarkdown,
    },
  );

/**
 * Checks if two EditorV3 content values have the same text but new value has no cursor.
 *
 * @param oldValue - The previous EditorV3 content value
 * @param newValue - The new EditorV3 content value to compare
 */
const sameTextNoCursor = (oldValue: EditorV3Content, newValue: EditorV3Content): boolean =>
  oldValue.caretPosition !== null &&
  newValue.caretPosition === null &&
  sameText(oldValue, newValue);

/**
 * Props for the EditorV3 component.
 *
 * @param id - Unique identifier for the editor
 * @param input - Initial content as string or editor interface
 * @param editable - Whether the editor is editable
 * @param setObject - Callback to update the editor content
 * @param customStyleMap - Custom style definitions
 * @param allowNewLine - Whether to allow multiple lines
 * @param textAlignment - Text alignment mode
 * @param decimalAlignPercent - Decimal alignment percentage
 * @param style - CSS properties for the editor
 * @param resize - Whether to allow resizing
 * @param noBorder - Whether to hide the border
 * @param spellCheck - Whether to enable spell checking
 * @param styleOnContextMenu - Whether to show style options on context menu
 * @param allowMarkdown - Whether to allow markdown syntax
 * @param allowWindowView - Whether to allow window view mode
 * @param markdownSettings - Markdown parsing settings
 * @param debounceMilliseconds - Debounce delay for updates
 * @param atListFunction - Function to fetch at-mention suggestions
 * @param maxAtListLength - Maximum number of at-mention suggestions
 */
export interface EditorV3Props extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  input: string | IEditorV3;
  editable?: boolean;
  setObject: (ret: IEditorV3) => void;
  customStyleMap?: EditorV3Styles;
  allowNewLine?: boolean;
  textAlignment?: EditorV3Align;
  decimalAlignPercent?: number;
  style?: CSSProperties;
  resize?: boolean | "vertical";
  noBorder?: boolean;
  spellCheck?: boolean;
  styleOnContextMenu?: boolean;
  allowMarkdown?: boolean;
  allowWindowView?: boolean;
  markdownSettings?: IMarkdownSettings;
  debounceMilliseconds?: number | null;
  atListFunction?: (at: string) => Promise<EditorV3DropListItem<Record<string, string>>[]>;
  maxAtListLength?: number;
}

/**
 * EditorV3 component for rich text editing.
 *
 * @param props - Destructured parameter object
 * @param props.id - Unique identifier applied to the editor instance and DOM element.
 * @param props.input - Initial content supplied to the editor; either a raw string or an `IEditorV3` object.
 * @param props.editable - When true, the editor accepts user input and exposes editing controls.
 * @param props.setObject - Callback invoked with an updated `IEditorV3` object when editor state changes.
 * @param props.style - Inline CSS properties applied to the editor container.
 * @param props.allowMarkdown - Enables Markdown parsing and rendering when true.
 * @param props.allowWindowView - Enables window-view mode for special content previews when true.
 * @param props.allowNewLine - Controls whether multi-line input (newlines) is permitted.
 * @param props.decimalAlignPercent - Percentage used for decimal alignment calculations within table cells.
 * @param props.markdownSettings - Configuration options controlling Markdown parsing behaviour.
 * @param props.customStyleMap - Optional style map (`EditorV3Styles`) to override default text styles.
 * @param props.textAlignment - Default text alignment mode for new content (`EditorV3Align`).
 * @param props.resize - Controls resizing behaviour; `true`/`false` or `'vertical'`.
 * @param props.noBorder - When true, the editor renders without a surrounding border.
 * @param props.spellCheck - Enables native browser spell checking when true.
 * @param props.styleOnContextMenu - When true, exposes style actions via the context menu.
 * @param props.debounceMilliseconds - Debounce delay in milliseconds for change propagation, or `null` for immediate.
 * @param props.atListFunction - Async function used to fetch at-mention suggestion items for the `@` completion list.
 * @param props.maxAtListLength - Maximum number of at-mention suggestions to display.
 */
export const EditorV3 = ({
  id,
  input,
  editable = true,
  setObject,
  style,
  allowMarkdown = defaultContentProps.allowMarkdown,
  allowWindowView = defaultContentProps.allowWindowView,
  allowNewLine = defaultContentProps.allowNewLine,
  decimalAlignPercent = defaultContentProps.decimalAlignPercent,
  markdownSettings = { ...defaultContentProps.markdownSettings },
  customStyleMap,
  textAlignment = defaultContentProps.textAlignment,
  resize = false,
  noBorder = false,
  spellCheck = false,
  styleOnContextMenu = true,
  debounceMilliseconds = null,
  atListFunction,
  maxAtListLength = 10,
  ...rest
}: EditorV3Props): React.ReactElement => {
  // Set up reference to main div
  const aiev3Ref = useRef<HTMLDivElement | null>(null);
  // Set up reference to inner div
  const editableRef = useRef<HTMLDivElement | null>(null);
  // other state variables
  const [showMarkdown, setShowMarkdown] = useState<boolean>(false);
  const [showWindowView, setShowWindowView] = useState<boolean>(false);
  const contentProps = useMemo((): EditorV3ContentPropsInput => {
    return {
      ...defaultContentProps,
      allowMarkdown,
      allowNewLine,
      decimalAlignPercent,
      styles: customStyleMap,
      markdownSettings,
      showMarkdown,
      textAlignment,
      atListFunction,
      maxAtListLength,
    };
  }, [
    allowMarkdown,
    allowNewLine,
    atListFunction,
    customStyleMap,
    decimalAlignPercent,
    markdownSettings,
    maxAtListLength,
    showMarkdown,
    textAlignment,
  ]);

  // General return function
  const [hasDrawnElement, setHasDrawnElement] = useState<boolean>(false);

  // #region Handling update
  // Redraw element, used in debounced stack as onChange callback
  const redrawElement = useCallback((ret: EditorV3Content) => {
    /* istanbul ignore else */
    if (editableRef.current) {
      ret.redraw(editableRef.current);
      // console.debug(
      //   "Redraw element ↓\n",
      //   ret.markdownText,
      //   "\nCaret position: ",
      //   ret.caretPosition,
      //   "\nSelection: ",
      //   window.getSelection()?.focusNode?.nodeName,
      //   window.getSelection()?.focusOffset,
      // );
    }
  }, []);

  // Create debounce stack
  const {
    currentValue,
    setCurrentValue,
    undo,
    redo,
    forceUpdate: forceReturn,
  } = useDebounceStack<EditorV3Content>({
    value: new EditorV3Content(input, {
      ...defaultContentProps,
      textAlignment,
      decimalAlignPercent,
      styles: customStyleMap,
      showMarkdown,
      markdownSettings,
      allowMarkdown,
      allowNewLine,
    }),
    // Send data base to the parent
    setValue: (ret) => setObject({ lines: ret.data.lines, contentProps: ret.data.contentProps }),
    debounceMilliseconds,
    onChange: redrawElement,
    comparisonFunction: sameText,
    blockReturnFunction: hasActiveAt,
    blockDebounceReturn: hasActiveAt,
    blockOnChange: sameTextNoCursor,
  });

  // Utility callback for debugging
  const setContent = useCallback(
    (newContent: EditorV3Content, _calledFrom?: string) => {
      // console.debug(
      //   "Set content ↓\n",
      //   newContent.markdownText,
      //   "\nCaret position: ",
      //   newContent.caretPosition,
      // );
      setCurrentValue(newContent);
    },
    [setCurrentValue],
  );
  // #endregion Handling update

  // #region Input utilities
  // Set up menu items
  const menuItems = useMemo((): IMenuItem[] => {
    // Add different styles
    const styleIMenuItem: IMenuItem[] =
      !editable || !contentProps.styles || Object.keys(contentProps.styles).length === 0
        ? []
        : [
            {
              label: "Style",
              disabled: contentProps.showMarkdown,
              group: [
                ...Object.keys(contentProps.styles).map((s) => {
                  return {
                    label: s,
                    disabled:
                      contentProps.showMarkdown ||
                      currentValue.isCaretLocked() ||
                      contentProps.styles?.[s].isNotAvailable ||
                      currentValue.caretPosition?.isCollapsed,
                    action: () => {
                      /* istanbul ignore else */
                      if (editableRef.current) {
                        const newContent = new EditorV3Content(editableRef.current, contentProps);
                        newContent.applyStyle(s);
                        setContent(newContent, "Apply style from menu");
                      }
                    },
                  };
                }),
                {
                  label: "Remove style",
                  disabled: contentProps.showMarkdown || currentValue.caretPosition?.isCollapsed,
                  action: () => {
                    /* istanbul ignore else */
                    if (editableRef.current) {
                      const newContent = new EditorV3Content(editableRef.current, contentProps);
                      newContent.removeStyle();
                      setContent(newContent, "Remove style from menu");
                    }
                  },
                },
              ],
            },
          ];

    // Add markdown switch
    const showMarkdownMenu = !allowMarkdown
      ? []
      : [
          {
            label: `${contentProps.showMarkdown ? "Hide" : "Show"} markdown`,
            action: () => {
              /* istanbul ignore else */
              if (editableRef.current) {
                const newShowMarkdown = !contentProps.showMarkdown;
                setShowMarkdown(newShowMarkdown);
                const newContent = new EditorV3Content(editableRef.current, {
                  ...contentProps,
                  showMarkdown: newShowMarkdown,
                });
                newContent.caretPosition = null; // Reset caret position
                setContent(newContent, "Markdown toggle");
              }
            },
          },
        ];

    // Add window view
    const showWindowViewMenu =
      !allowWindowView || !editable
        ? []
        : [
            {
              label: `${showWindowView ? "Hide" : "Show"} window view`,
              action: () => {
                /* istanbul ignore else */
                if (editableRef.current) {
                  const newContent = new EditorV3Content(editableRef.current, contentProps);
                  newContent.caretPosition = null;
                  setContent(newContent, "Window view menu");
                  setShowWindowView(!showWindowView);
                }
              },
            },
          ];

    return [...styleIMenuItem, ...showMarkdownMenu, ...showWindowViewMenu];
  }, [
    allowMarkdown,
    allowWindowView,
    contentProps,
    currentValue,
    editable,
    setContent,
    showWindowView,
  ]);

  // Create custom refresh event used by renderDropdown.
  const handleRefreshEvent = useCallback(
    (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      /* istanbul ignore else */
      if (editableRef.current) {
        const newContent = new EditorV3Content(editableRef.current, contentProps);
        /* istanbul ignore else */
        if (newContent.caretPosition) {
          newContent.caretPosition = {
            startLine: newContent.caretPosition.endLine,
            startChar: newContent.caretPosition.endChar,
            endLine: newContent.caretPosition.endLine,
            endChar: newContent.caretPosition.endChar,
          };
        }
        setContent(newContent, "Handle refresh event");
      }
    },
    [contentProps, setContent],
  );

  useEffect(() => {
    /* istanbul ignore else */
    if (editableRef.current) {
      const editableRefCurrent = editableRef.current;
      editableRefCurrent.addEventListener("editorv3refresh", handleRefreshEvent);
      return () => {
        editableRefCurrent?.removeEventListener("editorv3refresh", handleRefreshEvent);
      };
    }
  }, [handleRefreshEvent]);
  // #endregion Input utilities

  // #region Handle focus/blur
  // Focus and blur events are container not contenteditable level events!
  const handleFocus = useCallback((_e: React.FocusEvent | React.MouseEvent) => {
    // Dummy method - no longer required
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        e.target instanceof Node &&
        e.target === aiev3Ref.current &&
        editableRef.current &&
        editable
      ) {
        // Simulate click on editable div
        // As the target is editor, need to select all content
        const newContent = new EditorV3Content(editableRef.current, contentProps);
        // Select everything
        newContent.caretPositionF = {
          initialLine: 0,
          initialChar: 0,
          focusLine: newContent.lines.length - 1,
          focusChar: newContent.showMarkdown
            ? newContent.lines[newContent.lines.length - 1].lineMarkdown.length
            : newContent.lines[newContent.lines.length - 1].lineLength,
        };
        setContent(newContent, "aiev3 click select all");
        editableRef.current.focus();
      }
    },
    [contentProps, editable, setContent],
  );

  const handleBlur = useCallback(() => {
    /* istanbul ignore else */
    if (editableRef.current) {
      // Get current content (this should alreact be correct);
      const newContent = new EditorV3Content(editableRef.current, contentProps);
      // Select nothing
      newContent.caretPosition = null;
      forceReturn(newContent);
    }
  }, [contentProps, forceReturn]);
  // #endregion Handle focus/blur

  // #region Handle key/mouse events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      /* istanbul ignore else */
      if (editableRef.current && !contentProps.showMarkdown) {
        // Handle undo/redo
        /* istanbul ignore else */
        if (e.ctrlKey && e.code === "KeyZ") {
          e.preventDefault();
          undo();
        } else if (e.ctrlKey && e.code === "KeyY") {
          e.preventDefault();
          redo();
        }
        // Block (non-movement) key down when the caret is locked
        else if (
          currentValue.isCaretLocked() &&
          !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)
        ) {
          e.preventDefault();
        } else if (
          !(
            // Ignore key down on metakeys
            ["Control", "Shift", "Alt"].includes(e.key)
          ) &&
          !(e.ctrlKey && (e.code === "KeyC" || e.code === "KeyX" || e.code === "KeyV"))
        ) {
          // Get current information and update content buffer for positions
          const newContent = new EditorV3Content(editableRef.current, contentProps);
          newContent.handleKeydown(e);
          setContent(newContent, `Handle key down: ${e.key}`);
        }
      }
    },
    [contentProps, redo, setContent, currentValue, undo],
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      // Markdown editing should not be handled
      /* istanbul ignore else */
      if (editableRef.current && !currentValue.showMarkdown) {
        // Stop handled keys
        /* istanbul ignore else */
        if (["KeyY", "KeyZ"].includes(e.code) && e.ctrlKey) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        // These are handled on key down
        else if (["Backspace", "Delete"].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
        }
        // Always set new content value
        else if (
          !(
            // Ignore key up on metakeys
            ["Control", "Shift", "Alt"].includes(e.key)
          )
        ) {
          e.preventDefault();
          e.stopPropagation();
          const newContent = new EditorV3Content(editableRef.current, contentProps);
          newContent.handleKeyup(e);
          // Create @block if @ has been typed
          if (
            atListFunction &&
            newContent.caretPosition &&
            newContent.caretPosition.startChar > 0
          ) {
            const replacePos: EditorV3Position = {
              ...newContent.caretPosition,
              startChar: newContent.caretPosition.startChar - 1,
            };
            const lastChar = newContent
              .subLines(replacePos)
              .map((l) => l.lineText)
              .reduce((a, b) => (a += b), "");
            // Look for the @ character
            if (lastChar.trim() === "@") {
              // Do some @ magic here
              newContent.splice(replacePos, [
                new EditorV3Line([new EditorV3AtBlock({ text: "@", type: "at" })], contentProps),
              ]);
            }
          }
          setContent(newContent, `Handle key up: ${e.key}`);
        }
      }
    },
    [atListFunction, contentProps, setContent, currentValue],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Drop down list last entry can target again resize (current.parentElement)
      if (
        editableRef.current &&
        e.target instanceof Node &&
        (editableRef.current.parentElement === e.target ||
          editableRef.current === e.target ||
          editableRef.current.contains(e.target))
      ) {
        // Ensure mouse up event stack is empty before resolving this event
        setTimeout(() => {
          /* istanbul ignore else */
          if (editableRef.current) {
            const newContent = new EditorV3Content(editableRef.current, contentProps);
            newContent.checkStatus();
            setContent(newContent, "Handle mouse up on timeout");
          }
        }, 0);
      }
    },
    [contentProps, setContent],
  );
  // #endregion Handle key/mouse events

  // #region Handle clipboard events
  const handleCopy = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      /* istanbul ignore else */
      if (editableRef.current) {
        const newContent = new EditorV3Content(editableRef.current, contentProps);
        newContent.handleCopy(e);
        setContent(newContent, "Handle copy");
      }
    },
    [contentProps, setContent],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      /* istanbul ignore else */
      if (editableRef.current) {
        const newContent = new EditorV3Content(editableRef.current, contentProps);
        newContent.handlePaste(e);
        setContent(newContent, "Handle paste");
      }
    },
    [contentProps, setContent],
  );

  const handleBlock = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  // #endregion Handle clipboard events

  // #region Style recalculation
  const styleRecalc = useMemo(() => {
    const s = { ...style };
    switch (resize) {
      case "vertical":
        s.resize = "vertical";
        s.overflow = "auto";
        break;
      case true:
        s.resize = "both";
        s.overflow = "auto";
        break;
    }
    if (showWindowView) {
      s.backgroundColor = "rgba(0, 0, 0, 0.2)";
    }
    return s;
  }, [resize, showWindowView, style]);
  // #endregion Style recalculation

  // Initial render
  useEffect(() => {
    if (editableRef.current && !hasDrawnElement) {
      redrawElement(currentValue);
      setHasDrawnElement(true);
    }
  }, [hasDrawnElement, redrawElement, currentValue]);

  return (
    <>
      <div
        {...rest}
        ref={aiev3Ref}
        className={[
          "aiev3",
          editable ? "" : "disabled",
          currentValue.caretPosition !== null ? "editing" : "",
          showWindowView ? "window-view-active" : "",
          rest.className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
        id={id}
        onFocusCapture={(e) => {
          rest.onFocusCapture?.(e);
          handleFocus(e);
        }}
        onBlurCapture={(e) => {
          rest.onBlurCapture?.(e);
          handleBlur();
          window.getSelection()?.removeAllRanges();
          rest.onBlur?.(e);
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseUp={(e) => {
          handleMouseUp(e);
          rest.onMouseUp?.(e);
        }}
        onClick={(e) => {
          handleClick(e);
          rest.onClick?.(e);
        }}
      >
        <ContextMenuHandler
          menuItems={menuItems}
          style={{ width: "100%", height: "100%" }}
          showLowMenu={spellCheck || !styleOnContextMenu}
        >
          <div
            className={`aiev3-resize${!noBorder ? " aiev3-resize-border" : ""}`}
            style={styleRecalc}
          >
            <div
              id={`${id}-editable`}
              className={`aiev3-editing ${allowNewLine ? "multiline" : "singleline"}`}
              contentEditable={editable && !showWindowView}
              suppressContentEditableWarning
              role={editable ? "textbox" : undefined}
              spellCheck={spellCheck}
              ref={editableRef}
              onKeyDownCapture={handleKeyDown}
              onKeyUpCapture={handleKeyUp}
              onCutCapture={handleCopy}
              onCopyCapture={handleCopy}
              onPasteCapture={handlePaste}
              onDragStart={handleBlock}
              onDragOver={handleBlock}
              onDrop={handleBlock}
              onDragEnter={handleBlock}
              onDragLeave={handleBlock}
              onDragEnd={handleBlock}
            />
          </div>
        </ContextMenuHandler>
      </div>
      {allowWindowView && editable && (
        <WindowView
          id={`${id}-window`}
          showWindowView={showWindowView}
          setShowWindowView={setShowWindowView}
          state={currentValue}
          setState={(ret) => {
            forceReturn(ret);
            // Refocus editable after closing window view
            setTimeout(() => editableRef.current?.focus(), 10);
          }}
          includeAt={atListFunction !== undefined}
        />
      )}
    </>
  );
};

EditorV3.displayName = "AsupEditorV3";
